import { useEditor } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useRef } from 'react';
import styled, { css } from 'styled-components';

import { ActionButton, ActionsWrapper } from '../../styles/mainStyle';
import { useOutsideAlerter } from '../transcript/useClickOutside';

import type { IVideoCreatorStore } from '@src/stores/IVideoCreatorStore';

export type SecondaryActionKey =
  | 'clearVolumeKeyPoints'
  | 'deleteSelectedKeypoint';

const SECONDARY_ACTION_CONFIG: Record<
  SecondaryActionKey,
  {
    label: string;
    danger?: boolean;
    invoke: (vc: IVideoCreatorStore, elementId: string) => void;
  }
> = {
  deleteSelectedKeypoint: {
    label: 'Delete Key Point',
    danger: true,
    invoke: (vc, id) => vc.deleteSelectedVolumeKeyPoint(id),
  },
  clearVolumeKeyPoints: {
    label: 'Remove All Keyframes',
    danger: true,
    invoke: (vc, id) => vc.deleteAllVolumeKeyPoints(id),
  },
};

type Props = {
  action: 'copy' | 'paste';
  secondaryActions?: SecondaryActionKey[];
};
const TimelineContextMenu = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { editor } = useEditor();

  const contextRef = useRef<HTMLDivElement>(null);
  const { tmp, copied, pos, action, secondaryActions } =
    videoCreator.timelineClipboard || {};
  const { left, top } = pos || {};
  useOutsideAlerter(contextRef, () => {
    if (videoCreator.timelineClipboard) {
      videoCreator.timelineClipboard.pos = null;
    }
  });

  return (
    <Actions ref={contextRef} left={left} top={top} isVisible={!!pos}>
      {props.action === 'copy' && action === 'copy' && tmp && (
        <CopyElement
          onMouseDown={e => {
            // Prevent useOutsideAlerter from closing the menu before onClick fires
            e.stopPropagation();
          }}
          onClick={e => {
            e.stopPropagation();
            videoCreator.timelineClipboard!.pos = null;
            videoCreator.timelineClipboard!.copied = {
              element: tmp.element,
              data: null,
            };
          }}
        >
          <span>
            Copy{' '}
            {tmp.element.source.type.charAt(0).toUpperCase() +
              tmp.element.source.type.slice(1)}
          </span>
        </CopyElement>
      )}
      {props.action === 'paste' &&
        action === 'paste' &&
        copied?.element &&
        copied.data && (
          <PasteElement
            onMouseDown={e => {
              // Prevent useOutsideAlerter from closing the menu before onClick fires
              e.stopPropagation();
            }}
            onClick={async e => {
              e.stopPropagation();
              const { element, data } = copied;
              if (!data) return;
              editor.commandCreateAndAddElement(
                data.track.toString(),
                element?.source.type,
                {
                  ...element.source,
                  time: data.time,
                  track: data?.track,
                  // Strip the original id so the pasted element gets a fresh uuid.
                  id: undefined,
                },
              );
              videoCreator.timelineClipboard = null;
            }}
          >
            <span>
              Paste{' '}
              {copied.element.source.type.charAt(0).toUpperCase() +
                copied.element.source.type.slice(1)}
            </span>
          </PasteElement>
        )}
      {!!secondaryActions?.length &&
        tmp &&
        props.action === 'copy' &&
        action === 'copy' &&
        props.secondaryActions
          ?.filter(sa => secondaryActions.includes(sa))
          .map(sa => {
            const config = SECONDARY_ACTION_CONFIG[sa];
            if (!config) return null;
            return (
              <MenuAction
                key={sa}
                $danger={config.danger}
                onMouseDown={e => {
                  // Prevent useOutsideAlerter from closing the menu before onClick fires
                  e.stopPropagation();
                }}
                onClick={e => {
                  e.stopPropagation();
                  config.invoke(videoCreator, tmp.element.source.id);
                  videoCreator.timelineClipboard!.pos = null;
                }}
              >
                <span>{config.label}</span>
              </MenuAction>
            );
          })}
    </Actions>
  );
});

export default TimelineContextMenu;

// Approximate max menu height (up to 3 stacked ActionButtons). Used only to
// decide whether the menu would overflow the bottom of the viewport; the
// actual upward shift is height-agnostic via translateY(-100%).
const ESTIMATED_MENU_HEIGHT = 150;

const Actions = styled(ActionsWrapper)<{
  left: number | undefined;
  top: number | undefined;
}>`
  right: unset;
  position: fixed;
  top: ${props => props.top && `${props.top}px`};

  /* Vertical flip: open downward from the cursor by default, but anchor the
     menu's bottom edge to the cursor (open upward) when there isn't room
     below — e.g. right-clicking the music track, the lowest timeline track,
     near the bottom of the viewport, which otherwise pushes the menu
     offscreen. */
  ${props =>
    props.top &&
    props.top + ESTIMATED_MENU_HEIGHT >= window.innerHeight &&
    css`
      transform: translateY(-100%);
    `}

  ${props =>
    props.left &&
    props.left + 110 >= window.innerWidth &&
    css`
      right: 0;
    `}

  ${props =>
    props.left &&
    props.left + 110 < window.innerWidth &&
    css`
      left: ${props.left}px;
    `}

  z-index: 10000001;
  box-shadow: var(--elevation-toast);
  gap: 2px;
  padding: var(--spacing-xs);
`;

/* Context-menu rows use the standard rounded-row style and muted hover (see
   docs/design-system.md), overriding ActionButton's green action hover and
   between-row borders. */
const MenuAction = styled(ActionButton)<{ $danger?: boolean }>`
  border-radius: var(--radius-button);
  ${props => (props.$danger ? 'color: var(--destructive);' : '')}

  &:not(:first-child) {
    border-top: none;
  }

  &:hover {
    background-color: ${props =>
      props.$danger ? 'var(--destructive)' : 'var(--surface-elevated)'};
    color: ${props =>
      props.$danger ? 'var(--destructive-foreground)' : 'var(--foreground)'};
  }
`;

const CopyElement = styled(MenuAction)``;

const PasteElement = styled(MenuAction)``;
